import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL

export interface OpiFilters {
  date?: string
  startTime?: string
  endTime?: string
  languages?: string[]
  interpreters?: string[]
  customers?: number[]
}

export interface OpiLiveScorecards {
  totalOnline: number
  onCall: number
  available: number
  successRate: number
  totalCalls: number
  connectedCalls: number
  abandonedCalls: number
  backstopRate: number
  backstopSuccessRate: number
  transferredTotal: number
  effectiffCount: number
  bigwordCount: number
}

export interface OpiAbandonedLanguage {
  rank: number
  languageCode: string
  languageName: string
  abandonedCount: number
  lastNotified: string | null
}

export interface OpiVolumeExpected {
  rank: number
  languageCode: string
  languageName: string
  expectedCalls: number
  lastNotified: string | null
}

export interface OpiHourlySummary {
  hour: number
  hourLabel: string
  totalCalls: number
  successPercent: number
  abandonedPercent: number
  connected: number
  abandoned: number
  backstop: number
}

export interface OpiDashboardData {
  scorecards: OpiLiveScorecards
  topAbandonedLanguages: OpiAbandonedLanguage[]
  hourlySummary: OpiHourlySummary[]
  hourlyTotals: OpiHourlySummary
}

export interface OpiVolumeExpectedCache {
  volumeExpected: OpiVolumeExpected[]
  lastUpdated: string
  nextRefreshAt: string
  secondsUntilRefresh: number
}

export interface OpiHistoricalData {
  hourlySummary: OpiHourlySummary[]
  totals: OpiHourlySummary
}

export interface OpiLiveData {
  scorecards: OpiLiveScorecards
  topAbandonedLanguages: OpiAbandonedLanguage[]
  hourlySummary: OpiHourlySummary[]
  hourlyTotals: OpiHourlySummary
}

type OpiVolumeExpectedCacheRaw = {
  volumeExpected?: OpiVolumeExpected[]
  VolumeExpected?: OpiVolumeExpected[]
  lastUpdated?: string
  LastUpdated?: string
  nextRefreshAt?: string
  NextRefreshAt?: string
  secondsUntilRefresh?: number
  SecondsUntilRefresh?: number
}

function normalizeOpiVolumeExpectedCache(raw: OpiVolumeExpectedCacheRaw): OpiVolumeExpectedCache {
  return {
    volumeExpected: raw.volumeExpected ?? raw.VolumeExpected ?? [],
    lastUpdated: raw.lastUpdated ?? raw.LastUpdated ?? '',
    nextRefreshAt: raw.nextRefreshAt ?? raw.NextRefreshAt ?? '',
    secondsUntilRefresh: raw.secondsUntilRefresh ?? raw.SecondsUntilRefresh ?? 0,
  }
}

export async function getOpiDashboard(filters: OpiFilters = {}): Promise<OpiDashboardData> {
  const response = await axios.post(`${API_URL}/Dashboard/opi/overview`, filters)
  return response.data.data
}

/** Lightweight 30s refresh — no Volume Expected (served from cache). */
export async function getOpiLive(filters: OpiFilters = {}): Promise<OpiLiveData> {
  const response = await axios.post(`${API_URL}/Dashboard/opi/live`, filters)
  return response.data.data
}

export async function getOpiHistorical(filters: OpiFilters): Promise<OpiHistoricalData> {
  const response = await axios.post(`${API_URL}/Dashboard/opi/historical`, filters)
  return response.data.data
}

/** Cached Volume Expected — fast read, refreshed server-side every 60 minutes. */
export async function getOpiVolumeExpected(): Promise<OpiVolumeExpectedCache> {
  const response = await axios.post(`${API_URL}/Dashboard/opi/volume`)
  return normalizeOpiVolumeExpectedCache(response.data.data ?? {})
}

export async function sendOpiNotify(payload: {
  languageCode: string
  languageName: string
  notifySource: 'VolumeExpected' | 'AbandonedLanguage'
}): Promise<string> {
  const response = await axios.post(`${API_URL}/Dashboard/opi/notify`, payload)
  return response.data?.text
}
